import { Action, Reducer } from "redux";
import { GalleryService } from "../service/GalleryService";
import { AppThunkAction } from "./";
import { KnownAction } from "./actions/GalleryActions";

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface GalleryState {
  isLoading: boolean;
  isLoaded: boolean;
  galleryElements: GalleryElement[];
  parentId?: number;
  galleryElementsByParentId?: GalleryElement[];
  editGalleryElement?: GalleryElement;
  galleryElement?: GalleryElement;
  images?: string[];
  uploadedImageUrl?: string;
}

export interface GalleryElement {
  id: number;
  parentId: number;
  elementName: string;
  imageUrl: string;
  elementClassId: number;
  elementDescription?: string;
  htmlText?: string;
  link?: string;
  number: number;
}

export interface ElementClass {
  id: number;
  targetElement: string;
  className: string;
}

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
  requestGalleryElements:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.getGalleryElements();
      }
    },
  requestGalleryElementsByParentId:
    (parentId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.getGalleryElementsByParentId(parentId);
      }
    },
  deleteGalleryElement:
    (elementId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.deleteGalleryElement(elementId);
      }
    },
  updateGalleryElement:
    (element: GalleryElement): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.updateGalleryElement(element);
      }
    },
  getGalleryElement:
    (elementId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.getGalleryElement(elementId);
      }
    },
  getGalleryElementForEdit:
    (elementId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.getGalleryElementForEdit(elementId);
      }
    },
  createGalleryElement:
    (element: GalleryElement): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.createGalleryElement(element);
      }
    },
  uploadImages:
    (acceptedFile: File): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.uploadImage(acceptedFile);
      }
    },
  getImages: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (appState && appState.gallery && appState.user) {
      const service = new GalleryService(dispatch, appState);
      await service.getImages();
    }
  },
  resetEditGalleryElement:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        dispatch({
          type: "RESET_EDIT_GALLERY_ELEMENT",
        });
      }
    },
  resetGalleryElement:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        dispatch({
          type: "RESET_GALLERY_ELEMENT",
        });
      }
    },
  deleteImages:
    (images: string[]): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (appState && appState.gallery && appState.user) {
        const service = new GalleryService(dispatch, appState);
        await service.deleteImages(images);
      }
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const unloadedState: GalleryState = {
  galleryElements: [],
  isLoading: false,
  isLoaded: false,
};

export const reducer: Reducer<GalleryState> = (
  state: GalleryState | undefined,
  incomingAction: Action
): GalleryState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case "REQUEST_GALLERY_ELEMENTS":
      return {
        galleryElements: state.galleryElements,
        isLoading: true,
        isLoaded: false,
      };
    case "RECEIVE_GALLERY_ELEMENTS":
      return {
        galleryElements: action.galleryElements,
        isLoading: false,
        isLoaded: true,
      };
    case "REQUEST_GALLERY_ELEMENTS_BY_PARENTID":
      return {
        ...state,
        galleryElementsByParentId: state.galleryElementsByParentId,
        parentId: action.parentId,
      };
    case "RECEIVE_GALLERY_ELEMENTS_BY_PARENTID":
      return {
        ...state,
        galleryElementsByParentId: action.galleryElementsByParentId,
      };
    case "RECEIVE_DELETE_GALLERY_ELEMENT":
      if (action.succeed) {
        const updatedGalleryElements = state.galleryElementsByParentId?.filter(
          (g) => g.id !== action.galleryElement.id
        );

        return {
          ...state,
          galleryElementsByParentId: updatedGalleryElements,
        };
      }
      return state;
    case "RECEIVE_UPDATE_GALLERY_ELEMENT":
      if (action.succeed) {
        const indexOfUpdatedItem = state.galleryElementsByParentId?.findIndex(
          (g) => g.id === action.galleryElement.id
        );

        if (indexOfUpdatedItem !== undefined && indexOfUpdatedItem >= 0) {
          const updatedGalleryElements = [
            ...state.galleryElementsByParentId!.slice(0, indexOfUpdatedItem),
            action.galleryElement,
            ...state.galleryElementsByParentId!.slice(indexOfUpdatedItem + 1),
          ];

          return {
            ...state,
            galleryElementsByParentId: updatedGalleryElements,
          };
        }
      }

      return state;
    case "RECEIVE_GET_GALLERY_ELEMENT":
      return {
        ...state,
        galleryElement: action.galleryElement,
      };
    case "RECEIVE_GET_GALLERY_ELEMENT_FOR_EDIT":
      return {
        ...state,
        editGalleryElement: action.galleryElement,
      };
    case "RESET_EDIT_GALLERY_ELEMENT":
      return {
        ...state,
        editGalleryElement: undefined,
      };
    case "RESET_GALLERY_ELEMENT":
      return {
        ...state,
        galleryElement: undefined,
      };
    case "RECEIVE_CREATE_GALLERY_ELEMENT":
      if (action.succeed) {
        return {
          ...state,
          galleryElementsByParentId: state.galleryElementsByParentId
            ? state.galleryElementsByParentId.concat(action.galleryElements)
            : action.galleryElements,
        };
      }
      return state;
    case "RECEIVE_IMAGES":
      return action.succeed ? { ...state, images: action.images } : state;
    case "RECEIVE_UPLOAD_IMAGES":
      return action.succeed
        ? { ...state, uploadedImageUrl: action.uploadedImageUrl }
        : state;
    case "DELETE_IMAGES":
      return action.succeed
        ? {
            ...state,
            images: state.images?.filter((i) => !action.images.includes(i)),
          }
        : state;
    default:
      return state;
  }
};
